/**
 * Responsive design breakpoints for AI Stylist
 * Mobile-first approach with common device breakpoints
 */

export const Breakpoints = {
  /** Mobile devices (portrait): 0-479px */
  xs: 0,
  /** Mobile devices (landscape) / small tablets: 480-767px */
  sm: 480,
  /** Tablets (portrait): 768-1023px */
  md: 768,
  /** Tablets (landscape) / small desktops: 1024-1279px */
  lg: 1024,
  /** Desktops: 1280-1535px */
  xl: 1280,
  /** Large desktops: 1536px+ */
  xxl: 1536,
} as const;

export type BreakpointKey = keyof typeof Breakpoints;

/**
 * Media query helper functions for JS-side responsive logic
 * (e.g. useIsMobile hook). For Tailwind responsive styles, use the
 * sm:/md:/lg:/xl:/xxl: prefixes generated from theme.css breakpoint tokens.
 */
export const MediaQueries = {
  /** Min-width media query (mobile-first) */
  up: (breakpoint: BreakpointKey) => `@media (min-width: ${Breakpoints[breakpoint]}px)`,

  /** Max-width media query (desktop-first) */
  down: (breakpoint: BreakpointKey) => {
    const value = Breakpoints[breakpoint];
    return `@media (max-width: ${value > 0 ? value - 1 : 0}px)`;
  },

  /** Between two breakpoints */
  between: (min: BreakpointKey, max: BreakpointKey) => `@media (min-width: ${Breakpoints[min]}px) and (max-width: ${Breakpoints[max] - 1}px)`,

  /** Only at specific breakpoint */
  only: (breakpoint: BreakpointKey) => {
    const keys = Object.keys(Breakpoints) as BreakpointKey[];
    const index = keys.indexOf(breakpoint);
    const nextKey = keys[index + 1];

    if (nextKey) {
      return MediaQueries.between(breakpoint, nextKey);
    }
    return MediaQueries.up(breakpoint);
  },
} as const;

/**
 * Common media query strings for direct use
 */
export const MQ = {
  mobile: MediaQueries.down('md'),
  tablet: MediaQueries.between('md', 'lg'),
  desktop: MediaQueries.up('lg'),
  largeDesktop: MediaQueries.up('xl'),

  // Specific queries
  mobileOnly: MediaQueries.down('sm'),
  tabletAndUp: MediaQueries.up('md'),
  desktopAndUp: MediaQueries.up('lg'),

  // Touch vs pointer
  touch: '@media (hover: none) and (pointer: coarse)',
  pointer: '@media (hover: hover) and (pointer: fine)',

  // Orientation
  portrait: '@media (orientation: portrait)',
  landscape: '@media (orientation: landscape)',

  // Reduced motion preference
  reducedMotion: '@media (prefers-reduced-motion: reduce)',
} as const;

/**
 * Layout constants for the 3-panel design
 */
export const Layout = {
  /** Minimum width for side panels */
  sidePanelMinWidth: 280,
  /** Maximum width for side panels */
  sidePanelMaxWidth: 360,
  /** Sidebar panel percentage on desktop */
  sidePanelPercentage: 25,
  /** Header height */
  headerHeight: 56,
  /** Panel padding */
  panelPadding: 16,
  /** Gap between panels */
  panelGap: 8,
} as const;

/**
 * Breakpoint at which layout switches from 3-panel to single column
 */
export const LAYOUT_COLLAPSE_BREAKPOINT: BreakpointKey = 'lg';

/**
 * Mobile layout constants
 */
export const MobileLayout = {
  /** Bottom tab bar height (includes safe area padding) */
  bottomTabHeight: 56,
  /** Safe area bottom padding for home indicator */
  safeAreaBottom: 24,
  /** Total bottom bar height including safe area */
  bottomBarTotalHeight: 80, // 56 + 24
  /** Mobile header height */
  headerHeight: 56,
  /** Mobile content padding */
  contentPadding: 16,
  /** Bottom sheet height at half state (dvh) */
  bottomSheetHalf: '46dvh',
  /** Bottom sheet height at full state (dvh) */
  bottomSheetFull: '70dvh',
  /** Bottom sheet top corner radius */
  bottomSheetRadius: 20,
  /** Action bar height (px) */
  actionBarHeight: 64,
  /** Selection tags row height (px) */
  selectionTagsHeight: 44,
} as const;
